import React, { useState } from "react";
import { useMutation } from "~/hooks";
import {
  CREATE_AND_ADD_DISCOUNT,
  CREATE_AND_ADD_UPLIFT,
  DELETE_DISCOUNT,
  DELETE_UPLIFT,
  UPDATE_DISCOUNT,
  UPDATE_UPLIFT,
  UPDATE_DISCOUNTS,
  UPDATE_UPLIFTS,
  CREATE_AND_ADD_DISBURSEMENT,
  UPDATE_DISBURSEMENT,
  UPDATE_DISBURSEMENTS,
  DELETE_DISBURSEMENT,
  COPY_MATTER_PHASES,
  CREATE_PHASE,
  CREATE_PHASES,
  CREATE_TASK,
  CREATE_TASKS,
  CREATE_WORK,
  DELETE_PHASE,
  DELETE_PHASES,
  DELETE_TASK,
  DELETE_TASKS,
  DELETE_WORK,
  UPDATE_PHASE,
  UPDATE_PHASES,
  UPDATE_TASK,
  UPDATE_TASKS,
  UPDATE_WORK,
  COPY_LIBRARY_PHASE,
} from "../../../graphql/mutation";
import { UPDATE_ASSUMPTIONS } from "../../../graphql/mutation/assumption/update-assumption";
import { UPDATE_EXCLUSIONS } from "../../../graphql/mutation/exclusion/update-exclusion";
import {
  useMatterAssumptionsQuery,
  MATTER_ASSUMPTIONS_QUERY,
  MATTER_EXCLUSIONS_QUERY,
} from "../../../graphql/query";
import { DISCOUNT_FRAGMENT } from "../../../graphql/fragment/discounts";
import { UPLIFT_FRAGMENT } from "../../../graphql/fragment/uplifts";
import { DISBURSEMENT_FRAGMENT } from "../../../graphql/fragment/disbursements";
import { PHASE_FRAGMENT } from "../../../graphql/fragment/phase-fragment";
import { TASK_FRAGMENT } from "../../../graphql/fragment/task-fragment";
import { WORK_FRAGMENT } from "../../../graphql/fragment/work-fragment";
import {
  autoFocusDiscountLocalVar,
  autoFocusUpliftLocalVar,
  autoFocusDisbursementLocalVar,
  autoFocusPhaseLocalVar,
} from "../../../graphql/client/local-variables";
import {
  CreateAndAddUpliftMutation,
  CreateAndAddUpliftMutationVariables,
} from "../../../gql/graphql";
import {
  copyMatterPhases,
  copyMatterPhasesVariables,
} from "../../../graphql/__generated_types__/copyMatterPhases";
import {
  copyLibraryPhase,
  copyLibraryPhaseVariables,
} from "../../../graphql/__generated_types__/copyLibraryPhase";
import { findMatter, findMatterVariables } from "../../../graphql/__generated_types__/findMatter";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal, Typography } from "antd";
import { FIND_MATTER } from "../../../graphql/query";
import { mergeArraysOfObjectsByProp } from "../../../helpers";
import { createReorderingUpdates } from "../../create-reordering-updates";
import {
  Uplift,
  DeepPartial,
  DiscountType,
  UpliftType,
  Disbursement,
  TaxLabelStatus,
  Discount,
  Assumption,
  Exclusion,
  Task,
  PhaseBase,
} from "@design-system";
import { useMatterExclusionsQuery } from "../../../graphql/query";
import {
  useCreateAssumptionsMutation,
  useDeleteAssumptionsMutation,
  useCreateExclusionsMutation,
  useDeleteExclusionsMutation,
} from "../../../graphql/mutation";
import { useUpdateAssumptionMutation } from "../../../graphql/mutation/assumption/update-assumption";
import { useUpdateExclusionMutation } from "../../../graphql/mutation/exclusion/update-exclusion";
import { isClientId, newClientId } from "../../../graphql/client/client-id-generator";
import { translateGraphQLErrors } from "../../../error/graphql-errors";
import { translateError } from "../../../error/errors";
import { addPhaseToCache } from "../../scope/add-phase-to-cache";
import { setSingleOrRangeProps } from "../../../helpers/price-option";
import produce from "immer";

// Generic helper to create a reorder handler for any item type
function createReorderHandler(
  updateMutation: any,
  matterId: number,
  entityType: "discounts" | "uplifts" | "disbursements" | "assumptions" | "exclusions"
) {
  return (items: any[], activeId: number, overId: number) => {
    const { updates } = createReorderingUpdates(items, activeId, overId);

    // Different structure for assumptions
    if (entityType === "assumptions") {
      return updateMutation({
        variables: {
          input: updates.map((u) => ({
            id: u.id,
            payload: {
              order: { set: u.order },
            },
          })),
        },
        update: (cache: any) => {
          cache.updateQuery(
            {
              query: MATTER_ASSUMPTIONS_QUERY,
              variables: { input: matterId },
            },
            (currentData: any): any => {
              return {
                matterAssumptions: mergeArraysOfObjectsByProp(
                  currentData?.matterAssumptions || [],
                  updates as any[],
                  "id"
                ),
              };
            }
          );
        },
      });
    }

    // Special structure for exclusions
    if (entityType === "exclusions") {
      return updateMutation({
        variables: {
          input: updates.map((u) => ({
            id: u.id,
            data: {
              order: { set: u.order },
            },
          })),
        },
        update: (cache: any) => {
          cache.updateQuery(
            {
              query: MATTER_EXCLUSIONS_QUERY,
              variables: { input: matterId },
            },
            (currentData: any): any => {
              return {
                matterExclusions: mergeArraysOfObjectsByProp(
                  currentData?.matterExclusions || [],
                  updates as any[],
                  "id"
                ),
              };
            }
          );
        },
      });
    }

    // Original structure for other entity types
    return updateMutation({
      variables: {
        data: updates.map((u) => ({
          data: { order: { set: u.order } },
          id: u.id,
        })),
      },
      update: (cache: any) => {
        cache.updateQuery(
          { query: FIND_MATTER, variables: { id: matterId } },
          (currentData: any): any => {
            if (currentData?.findMatter) {
              return {
                findMatter: {
                  ...currentData.findMatter,
                  [entityType]: mergeArraysOfObjectsByProp(
                    currentData?.findMatter[entityType] || [],
                    updates,
                    "id"
                  ),
                },
              };
            }
          }
        );
      },
      optimisticResponse: {
        [entityType === "discounts"
          ? "updateDiscounts"
          : entityType === "uplifts"
          ? "updateUplifts"
          : "updateDisbursements"]: updates.map((u) => ({
          ...u,
          __typename:
            entityType === "discounts"
              ? "Discount"
              : entityType === "uplifts"
              ? "Uplift"
              : "Disbursement",
        })),
      },
    });
  };
}

// Custom hook for managing disbursement mutations
export function useDisbursementMutations(matterId: number) {
  const [createDisbursementMutation] = useMutation(CREATE_AND_ADD_DISBURSEMENT, {
    update(cache, { data }) {
      if (!data) return;

      const newDisbursement = {
        ...data.createAndAddDisbursement,
        id: data.createAndAddDisbursement.id,
        autoFocus: true,
      };
      const newDisbursementRef = cache.writeFragment({
        data: newDisbursement,
        fragment: DISBURSEMENT_FRAGMENT,
      });
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          disbursements(existingDisbursementRefs = [], { readField }) {
            if (
              existingDisbursementRefs.some(
                (ref: any) => readField("id", ref) === data.createAndAddDisbursement.id
              )
            ) {
              return existingDisbursementRefs;
            }
            return [...existingDisbursementRefs, newDisbursementRef];
          },
        },
      });
      autoFocusDisbursementLocalVar(true);
    },
  });

  const [updateDisbursementMutation] = useMutation(UPDATE_DISBURSEMENT);

  const [deleteDisbursementMutation] = useMutation(DELETE_DISBURSEMENT, {
    update(cache, { data }) {
      if (!data) return;

      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          disbursements(existingDisbursementRefs = [], { readField }) {
            return existingDisbursementRefs.filter(
              (disbursementRef: any) =>
                data.deleteDisbursement.disbursementId !== readField("id", disbursementRef)
            );
          },
        },
      });
    },
  });

  const [updateDisbursements] = useMutation(UPDATE_DISBURSEMENTS);

  // Create a reusable reorder handler
  const reorderDisbursements = createReorderHandler(updateDisbursements, matterId, "disbursements");

  const autoFocusOff = (): void => {
    autoFocusDisbursementLocalVar(false);
  };

  // Wrap the mutation functions to match expected prop types
  const createDisbursementFromLibrary = (disbursement?: Partial<Disbursement>) => {
    return createDisbursementMutation({
      variables: {
        data: {
          name: disbursement?.name || "",
          amount: disbursement?.amount || 0,
          matter: { connect: { id: matterId } },
          order: disbursement?.order || 0,
          vatStatus: disbursement?.vatStatus || TaxLabelStatus.Inclusive,
        },
      },
    });
  };

  const updateDisbursement = (data: DeepPartial<Disbursement>) => {
    if (!data?.id) return false;

    return updateDisbursementMutation({
      variables: {
        disbursement: {
          id: data.id,
          data: {
            ...(data.amount !== undefined && { amount: { set: data.amount } }),
            ...(data.name && { name: { set: data.name } }),
            ...(data.vatStatus && { vatStatus: { set: data.vatStatus } }),
          },
        },
      },
      optimisticResponse: {
        updateDisbursement: {
          __typename: "Disbursement",
          id: data.id,
          amount: data.amount || 0,
          name: data.name || "",
          vatStatus: data.vatStatus || TaxLabelStatus.Inclusive,
          order: data.order || 0,
          updatedAt: new Date().toISOString(),
        },
      },
    });
  };

  const confirmDeleteDisbursement = (disbursementId: number) => {
    Modal.confirm({
      width: 600,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Typography.Text>{`Are you sure you want to delete this disbursement?`}</Typography.Text>
      ),
      onOk: async function () {
        deleteDisbursementMutation({
          variables: { disbursementId },
          optimisticResponse: {
            deleteDisbursement: {
              disbursementId,
              __typename: "DeleteDisbursementResult",
            },
          },
        });
      },
      onCancel() {},
    });
  };

  return {
    createDisbursement: createDisbursementMutation,
    updateDisbursement,
    deleteDisbursement: confirmDeleteDisbursement,
    createDisbursementFromLibrary,
    onReorder: reorderDisbursements,
    autoFocusOff,
  };
}

// Custom hook for managing discount mutations
export function useDiscountMutations(matterId: number) {
  const [createDiscountMutation] = useMutation(CREATE_AND_ADD_DISCOUNT, {
    update(cache, { data }) {
      if (!data) {
        return;
      }

      const { createAndAddDiscount } = data;
      const newDiscountRef = cache.writeFragment({
        data: createAndAddDiscount,
        fragment: DISCOUNT_FRAGMENT,
      });
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          discounts(existingDiscountRefs = [], { readField }) {
            if (
              existingDiscountRefs.some(
                (ref: any) => readField("id", ref) === createAndAddDiscount.id
              )
            ) {
              return existingDiscountRefs;
            }
            return [...existingDiscountRefs, newDiscountRef];
          },
        },
      });
      autoFocusDiscountLocalVar(true);
    },
  });

  const [updateDiscountMutation] = useMutation(UPDATE_DISCOUNT);

  const [deleteDiscountMutation] = useMutation(DELETE_DISCOUNT, {
    update(cache, { data }) {
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          discounts(existingDiscountRefs = [], { readField }) {
            return existingDiscountRefs.filter(
              (discountRef: any) => data?.deleteDiscount.discountId !== readField("id", discountRef)
            );
          },
        },
      });
    },
  });

  const [updateDiscounts] = useMutation(UPDATE_DISCOUNTS);

  // Create a reusable reorder handler
  const reorderDiscounts = createReorderHandler(updateDiscounts, matterId, "discounts");

  const autoFocusOff = (): void => {
    autoFocusDiscountLocalVar(false);
  };

  // Wrap the mutation functions to match the expected prop types for MatterDiscountsEditor
  const createDiscountFromLibrary = (data?: any) => {
    const reason = typeof data === "string" ? data : data?.reason || "";
    return createDiscountMutation({
      variables: {
        matterId,
        data: {
          matter: { connect: { id: matterId } },
          reason: reason,
          amount: data?.amount || 0,
          type: data?.type || DiscountType.Percent,
          order: data?.order || 0,
        },
      },
    });
  };

  const updateDiscount = (data: DeepPartial<Discount>) => {
    if (!data?.id) return false;

    return updateDiscountMutation({
      variables: {
        id: data.id,
        data: {
          ...(data.reason && { reason: { set: data.reason } }),
          ...(data.type && { type: { set: data.type } }),
          ...(data.amount !== undefined && { amount: { set: data.amount } }),
          ...(data.order && { order: { set: data.order } }),
        },
      },
      optimisticResponse: {
        updateDiscount: {
          __typename: "Discount",
          id: data.id,
          reason: data.reason || "",
          amount: data.amount ?? 0,
          type: data.type || DiscountType.Percent,
          order: data.order || 0,
          autoFocus: data.autoFocus ?? null,
        },
      },
    });
  };

  const confirmDeleteDiscount = (discountId: number) => {
    Modal.confirm({
      width: 600,
      icon: <ExclamationCircleOutlined />,
      content: (
        <Typography.Text>{`Are you sure you want to delete this discount?`}</Typography.Text>
      ),
      onOk: async function () {
        deleteDiscountMutation({
          variables: { discountId },
          optimisticResponse: {
            deleteDiscount: { discountId, __typename: "DeleteDiscountResult" },
          },
        });
      },
      onCancel() {},
    });
  };

  return {
    createDiscount: createDiscountMutation,
    updateDiscount,
    deleteDiscount: confirmDeleteDiscount,
    createDiscountFromLibrary,
    onReorder: reorderDiscounts,
    autoFocusOff,
  };
}

// Custom hook for managing uplift mutations
export function useUpliftMutations(matterId: number) {
  const [createUpliftMutation] = useMutation<
    CreateAndAddUpliftMutation,
    CreateAndAddUpliftMutationVariables
  >(CREATE_AND_ADD_UPLIFT, {
    update(cache, { data }) {
      const newUpliftRef = cache.writeFragment({
        data: data?.createAndAddUplift,
        fragment: UPLIFT_FRAGMENT,
      });
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          uplifts(existingUpliftRefs = [], { readField }) {
            if (
              existingUpliftRefs.some(
                (ref: any) => readField("id", ref) === data?.createAndAddUplift.id
              )
            ) {
              return existingUpliftRefs;
            }
            return [...existingUpliftRefs, newUpliftRef];
          },
        },
      });
      autoFocusUpliftLocalVar(true);
    },
  });

  const [updateUpliftMutation] = useMutation(UPDATE_UPLIFT);

  const [deleteUpliftMutation] = useMutation(DELETE_UPLIFT, {
    update(cache, { data }) {
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          uplifts(existingUpliftRefs = [], { readField }) {
            return existingUpliftRefs.filter(
              (upliftRef: any) => data?.deleteUplift.upliftId !== readField("id", upliftRef)
            );
          },
        },
      });
    },
  });

  const [updateUplifts] = useMutation(UPDATE_UPLIFTS);

  // Create a reusable reorder handler
  const reorderUplifts = createReorderHandler(updateUplifts, matterId, "uplifts");

  const autoFocusOff = (): void => {
    autoFocusUpliftLocalVar(false);
  };

  // Wrap the mutation functions to match expected prop types
  const createUpliftFromLibrary = (uplift?: DeepPartial<Uplift>) => {
    return createUpliftMutation({
      variables: {
        matterId,
        data: {
          libraryUplift: { connect: { id: uplift?.baseUplift?.id || uplift?.id } },
          amount: uplift?.amount ?? 0,
          matter: { connect: { id: matterId } },
          type: uplift?.type || UpliftType.Percent,
          order: uplift?.order ?? 0, // This will be updated later based on the current uplifts length
        },
      },
    });
  };

  const updateUplift = (data: DeepPartial<Uplift>) => {
    if (!data?.id) return false;

    return updateUpliftMutation({
      variables: {
        id: data.id,
        data: {
          ...(data.amount !== undefined && { amount: { set: data.amount } }),
          ...(data.type && { type: { set: data.type } }),
          ...(data.order && { order: { set: data.order } }),
          ...(data.includeOnProposal !== undefined && {
            includeOnProposal: { set: data.includeOnProposal },
          }),
        },
      },
      optimisticResponse: {
        updateUplift: {
          __typename: "Uplift",
          id: data.id,
          amount: data.amount ?? 0,
          type: data.type ?? UpliftType.Percent,
          order: data.order ?? 0,
          autoFocus: data.autoFocus ?? null,
          includeOnProposal: data.includeOnProposal ?? null,
          baseUplift: data.baseUplift
            ? {
                __typename: "LibraryUplift",
                id: data.baseUplift.id ?? 0,
                reason: data.baseUplift.reason ?? "",
              }
            : null,
        },
      },
    });
  };

  const confirmDeleteUplift = (upliftId: number) => {
    Modal.confirm({
      width: 600,
      icon: <ExclamationCircleOutlined />,
      content: <Typography.Text>{`Are you sure you want to delete this uplift?`}</Typography.Text>,
      onOk: async function () {
        deleteUpliftMutation({
          variables: { upliftId },
          optimisticResponse: { deleteUplift: { upliftId, __typename: "DeleteUpliftResult" } },
        });
      },
      onCancel() {},
    });
  };

  return {
    createUplift: createUpliftMutation,
    updateUplift,
    deleteUplift: confirmDeleteUplift,
    createUpliftFromLibrary,
    onReorder: reorderUplifts,
    autoFocusOff,
  };
}

export function useAssumptionsMutations(matterId: number) {
  const { data: matterAssumptionsData, loading: matterAssumptionsLoading } =
    useMatterAssumptionsQuery(matterId);
  const remoteAssumptions = matterAssumptionsData?.matterAssumptions || [];

  const [newAssumptions, setNewAssumptions] = useState<Assumption[]>([]);

  const [createAssumptionMutation] = useCreateAssumptionsMutation({}, remoteAssumptions.length);
  const [updateAssumptionMutation] = useUpdateAssumptionMutation();
  const [deleteAssumptionMutation] = useDeleteAssumptionsMutation();
  const [updateAssumptions] = useMutation(UPDATE_ASSUMPTIONS);

  // Create a reusable reorder handler for assumptions
  const reorderAssumptions = createReorderHandler(updateAssumptions, matterId, "assumptions");

  const removeNewAssumption = (id: number) => {
    setNewAssumptions((prev) => prev.filter((a) => a.id !== id));
  };

  const addNew = () => {
    const newItem: Assumption = {
      id: newClientId(),
      text: "",
      order: remoteAssumptions.length + newAssumptions.length,
      organisationalUnits: [],
    };
    setNewAssumptions([newItem, ...newAssumptions]);
  };

  const createAssumption = async ({
    text,
    mandatory,
    id: libraryAssumptionId,
    fromLibraryAssumptionId,
  }: Partial<Assumption>) => {
    let result;
    try {
      result = await createAssumptionMutation(matterId, {
        text: text || "",
        matter: { connect: { id: matterId } },
        order: remoteAssumptions.length + newAssumptions.length,
        mandatory: mandatory || false,
        // If the assumption is from the library, connect it to the library assumption
        ...(libraryAssumptionId &&
          libraryAssumptionId > 0 && {
            fromLibraryAssumption: { connect: { id: libraryAssumptionId } },
          }),
        // If the assumption is from a historical matter, inherit the library assumption id
        ...(fromLibraryAssumptionId &&
          fromLibraryAssumptionId > 0 && {
            fromLibraryAssumption: { connect: { id: fromLibraryAssumptionId } },
          }),
      });
    } catch (error) {
      throw translateError(error as any, "Failed to save Assumption");
    }
    if (result && result.errors) {
      throw translateGraphQLErrors(result.errors, "Failed to save Assumption");
    }
  };

  const updateAssumption = async (assumption: Assumption, text: string) => {
    if (isClientId(assumption.id)) {
      removeNewAssumption(assumption.id);
      await createAssumption({ text, mandatory: assumption.mandatory, id: undefined });
    } else {
      let result;
      try {
        result = await updateAssumptionMutation(matterId, {
          ...assumption,
          text,
        });
      } catch (error) {
        throw translateError(error as any, "Failed to update Assumption");
      }
      if (result && result.errors) {
        throw translateGraphQLErrors(result.errors, "Failed to update Assumption");
      }
    }
  };

  const deleteAssumption = async ({ id }: Partial<Assumption>) => {
    if (!id) return;
    if (isClientId(id)) {
      removeNewAssumption(id);
      return;
    }
    await deleteAssumptionMutation(matterId, id);
  };

  return {
    matterAssumptions: [...remoteAssumptions, ...newAssumptions],
    loading: {
      matterAssumptionsLoading,
    },
    onAddNew: addNew,
    onCopy: createAssumption,
    onSave: updateAssumption,
    onDelete: deleteAssumption,
    onReorder: reorderAssumptions,
  };
}

export function useExclusionsMutations(matterId: number) {
  const { data: matterExclusionsData, loading: matterExclusionsLoading } =
    useMatterExclusionsQuery(matterId);
  const remoteExclusions = matterExclusionsData?.matterExclusions || [];

  const [newExclusions, setNewExclusions] = useState<Exclusion[]>([]);

  const [createExclusionMutation] = useCreateExclusionsMutation({}, remoteExclusions.length);
  const [updateExclusionMutation] = useUpdateExclusionMutation();
  const [deleteExclusionMutation] = useDeleteExclusionsMutation();
  const [updateExclusions] = useMutation(UPDATE_EXCLUSIONS);

  // Create a reusable reorder handler for exclusions
  const reorderExclusions = createReorderHandler(updateExclusions, matterId, "exclusions");

  const removeNewExclusion = (id: number) => {
    setNewExclusions((prev) => prev.filter((a) => a.id !== id));
  };

  const addNew = () => {
    const newItem: Exclusion = {
      id: newClientId(),
      text: "",
      order: remoteExclusions.length + newExclusions.length,
      organisationalUnits: [],
    };
    setNewExclusions([newItem, ...newExclusions]);
  };

  const createExclusion = async ({
    text,
    mandatory,
    id: libraryExclusionId,
    fromLibraryExclusionId,
  }: Partial<Exclusion>) => {
    let result;
    try {
      result = await createExclusionMutation(matterId, {
        text: text || "",
        matter: { connect: { id: matterId } },
        order: remoteExclusions.length + newExclusions.length,
        mandatory: mandatory || false,
        // If the exclusion is from the library, connect it to the library exclusion
        ...(libraryExclusionId &&
          libraryExclusionId > 0 && {
            fromLibraryExclusion: { connect: { id: libraryExclusionId } },
          }),
        // If the exclusion is from a historical matter, inherit the library exclusion id
        ...(fromLibraryExclusionId &&
          fromLibraryExclusionId > 0 && {
            fromLibraryExclusion: { connect: { id: fromLibraryExclusionId } },
          }),
      });
    } catch (error) {
      throw translateError(error as any, "Failed to save Exclusion");
    }
    if (result && result.errors) {
      throw translateGraphQLErrors(result.errors, "Failed to save Exclusion");
    }
  };

  const updateExclusion = async (exclusion: Exclusion, text: string) => {
    if (isClientId(exclusion.id)) {
      removeNewExclusion(exclusion.id);
      await createExclusion({ text, mandatory: exclusion.mandatory, id: undefined });
    } else {
      let result;
      try {
        result = await updateExclusionMutation(matterId, {
          ...exclusion,
          text,
        });
      } catch (error) {
        throw translateError(error as any, "Failed to update Exclusion");
      }
      if (result && result.errors) {
        throw translateGraphQLErrors(result.errors, "Failed to update Exclusion");
      }
    }
  };

  const deleteExclusion = async ({ id }: Partial<Exclusion>) => {
    if (!id) return;
    if (isClientId(id)) {
      removeNewExclusion(id);
      return;
    }
    await deleteExclusionMutation(matterId, id);
  };

  return {
    matterExclusions: [...remoteExclusions, ...newExclusions],
    loading: {
      matterExclusionsLoading,
    },
    onAddNew: addNew,
    onCopy: createExclusion,
    onSave: updateExclusion,
    onDelete: deleteExclusion,
    onReorder: reorderExclusions,
  };
}

/**
 * Custom hook for managing copy phases mutation
 * This allows copying all phases and tasks from a source matter to the target matter
 */
export function useScopeOfWorkMutations(matterId: number) {
  const [copyPhasesMutation, { loading: copyPhasesLoading }] = useMutation<
    copyMatterPhases,
    copyMatterPhasesVariables
  >(COPY_MATTER_PHASES, {
    onCompleted: (data) => {
      if (!data?.copyMatterPhases) {
        console.error("No data returned from copyMatterPhases mutation");
      }
    },
    onError: (error) => {
      console.error("Error copying phases from matter:", error);
    },
  });

  const [copyPhaseToMatterMutation, { loading: copyPhaseLoading }] = useMutation<
    copyLibraryPhase,
    copyLibraryPhaseVariables
  >(COPY_LIBRARY_PHASE, {
    update(cache, { data }) {
      const copiedLibraryPhase = data?.copyLibraryPhase;
      if (!copiedLibraryPhase) {
        return;
      }

      // Get current phases to find max order
      const currentData = cache.readQuery<findMatter>({
        query: FIND_MATTER,
        variables: { id: matterId },
      });

      const existingPhases = currentData?.findMatter?.phases || [];
      const maxOrder =
        existingPhases.length > 0 ? Math.max(...existingPhases.map((p) => p.order || 0)) : -1;

      // Set new phase order to be max + 1
      const newOrder = maxOrder + 1;

      // Add the phase to the cache with updated order
      addPhaseToCache(
        cache,
        {
          ...(copiedLibraryPhase.phase as any),
          order: newOrder,
        },
        matterId
      );

      // After copying the phase, update its order
      updatePhase({
        variables: {
          phaseId: copiedLibraryPhase.phase.id,
          matterId,
          data: {
            order: { set: newOrder },
          },
        },
      });

      // Update assumptions
      cache.updateQuery(
        { query: MATTER_ASSUMPTIONS_QUERY, variables: { input: matterId } },
        (currentData: any) => {
          if (!currentData) return;
          const existingAssumptions = currentData.matterAssumptions || [];
          const newAssumptions = copiedLibraryPhase.assumptions.map((x: any) => ({
            ...x,
            __typename: "Assumption",
            autoFocus: false,
            default: false,
            matterId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            mandatory: false,
          }));
          return {
            matterAssumptions: [...existingAssumptions, ...newAssumptions],
          };
        }
      );

      // Update exclusions
      cache.updateQuery(
        { query: MATTER_EXCLUSIONS_QUERY, variables: { input: matterId } },
        (currentData: any) => {
          if (!currentData) return;
          const existingExclusions = currentData.matterExclusions || [];
          const newExclusions = copiedLibraryPhase.exclusions.map((x: any) => ({
            ...x,
            __typename: "Exclusion",
            autoFocus: false,
            default: false,
            matterId,
            mandatory: false,
            fromLibraryExclusionId: null,
          }));
          return {
            matterExclusions: [...existingExclusions, ...newExclusions],
          };
        }
      );

      // Update disbursements if available
      if (copiedLibraryPhase.disbursements && copiedLibraryPhase.disbursements.length > 0) {
        cache.updateQuery(
          { query: FIND_MATTER, variables: { id: matterId } },
          (currentData: any) => {
            if (!currentData?.findMatter) return;
            const existingDisbursements = currentData.findMatter.disbursements || [];
            return {
              findMatter: {
                ...currentData.findMatter,
                disbursements: [...existingDisbursements, ...copiedLibraryPhase.disbursements],
              },
            };
          }
        );
      }
    },
  });

  // Phase mutations
  const [createPhase, { loading: createPhaseLoading }] = useMutation(CREATE_PHASE, {
    update(cache, { data }) {
      addPhaseToCache(cache, data!.createAndAddPhase, matterId);
      autoFocusPhaseLocalVar(true);
    },
  });

  const [createPhases, { loading: createPhasesLoading }] = useMutation(CREATE_PHASES, {
    update(cache, { data }) {
      const newPhases = data?.createAndAddPhases;
      if (!newPhases) return;
      newPhases.forEach((p) => addPhaseToCache(cache, p, matterId));
      autoFocusPhaseLocalVar(true);
    },
  });

  const [updatePhase, { loading: updatePhaseLoading }] = useMutation(UPDATE_PHASE);
  const [updatePhases, { loading: updatePhasesLoading }] = useMutation(UPDATE_PHASES);

  const [deletePhase, { loading: deletePhaseLoading }] = useMutation(DELETE_PHASE, {
    update(cache, { data }) {
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          phases(existingPhaseRefs = [], { readField }) {
            return existingPhaseRefs.filter(
              (phaseRef: any) => data?.deletePhase.phaseId !== readField("id", phaseRef)
            );
          },
        },
      });
    },
  });

  const [deletePhases, { loading: deletePhasesLoading }] = useMutation(DELETE_PHASES, {
    update(cache, { data }) {
      const deletedPhaseIds = data?.deletePhases.phasesIds;
      if (!deletedPhaseIds) return;
      deletedPhaseIds.forEach((phaseId) => {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases(existingPhaseRefs = [], { readField }) {
              return existingPhaseRefs.filter(
                (phaseRef: any) => phaseId !== readField("id", phaseRef)
              );
            },
          },
        });
      });
    },
  });

  // Task mutations
  const [createTask, { loading: createTaskLoading }] = useMutation(CREATE_TASK);
  const [createTasks, { loading: createTasksLoading }] = useMutation(CREATE_TASKS);
  const [updateTask, { loading: updateTaskLoading }] = useMutation(UPDATE_TASK);
  const [updateTasks, { loading: updateTasksLoading }] = useMutation(UPDATE_TASKS);
  const [deleteTask, { loading: deleteTaskLoading }] = useMutation(DELETE_TASK);
  const [deleteTasks, { loading: deleteTasksLoading }] = useMutation(DELETE_TASKS);

  // Work (fee earner) mutations
  const [createWork, { loading: createWorkLoading }] = useMutation(CREATE_WORK);
  const [updateWork, { loading: updateWorkLoading }] = useMutation(UPDATE_WORK);
  const [deleteWork, { loading: deleteWorkLoading }] = useMutation(DELETE_WORK);

  // Helper functions
  const autoFocusOff = (): void => {
    autoFocusPhaseLocalVar(false);
  };

  // Sorting helpers
  const sortById = (a: { id: number }, b: { id: number }) => b.id - a.id;
  const sortByOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;

  // Task cache helpers
  const createTaskCacheAdder = (phaseId: number, order?: number) => (cache: any, newItem: any) => {
    const newTask = newItem.data.createAndAddTask;
    const newTaskRef = cache.writeFragment({
      data: { ...newTask, order },
      fragment: TASK_FRAGMENT,
    });
    cache.modify({
      id: `Phase:${phaseId}`,
      fields: {
        tasks(existingTaskRefs = [], { readField }: any) {
          if (existingTaskRefs.some((ref: any) => readField("id", ref) === newTask.id)) {
            return existingTaskRefs;
          }
          return [...existingTaskRefs, newTaskRef];
        },
      },
    });
  };

  const createTaskCacheRemover =
    (phaseId: number) =>
    (cache: any, { data: { deleteTask } }: any) => {
      cache.modify({
        id: `Phase:${phaseId}`,
        fields: {
          tasks(existingTaskRefs = [], { readField }: any) {
            return existingTaskRefs.filter(
              (taskRef: any) => deleteTask.taskId !== readField("id", taskRef)
            );
          },
        },
      });
    };

  const createWorkCacheRemover =
    (phaseId: number) =>
    (cache: any, { data: { deleteWork } }: any) => {
      cache.modify({
        id: `Phase:${phaseId}`,
        fields: {
          work(existingWorkRefs = [], { readField }: any) {
            return existingWorkRefs.filter(
              (workRef: any) => deleteWork.workId !== readField("id", workRef)
            );
          },
        },
      });
    };

  // Reordering functions
  const onReorderPhase = async (phases: any[], activeId: number, overId: number) => {
    const { updates } = createReorderingUpdates<{
      title: string;
      description: string | null;
      priceOptions?: any;
      order: number;
      id: number;
      autoFocus: boolean | null;
    }>(phases, activeId, overId);

    await updatePhases({
      variables: {
        matterId,
        data: updates.map((u) => ({
          id: u.id,
          payload: {
            order: { set: u.order },
          },
        })),
      },
      update: (cache) => {
        cache.updateQuery<findMatter, findMatterVariables>(
          { query: FIND_MATTER, variables: { id: matterId } },
          (currentData): findMatter | void => {
            if (currentData?.findMatter) {
              return {
                findMatter: {
                  ...currentData.findMatter,
                  phases: mergeArraysOfObjectsByProp(
                    currentData?.findMatter.phases || [],
                    updates as any,
                    "id"
                  ),
                },
              };
            }
          }
        );
      },
    });
  };

  const onReorderTask = async (tasks: any[], activeId: number | null, overId: number | null) => {
    if (!activeId || !overId || !tasks || tasks.length === 0) {
      console.error("Invalid parameters for task reordering");
      return;
    }

    const { updates } = createReorderingUpdates<Task>(tasks, activeId, overId);
    await updateTasks({
      variables: {
        data: updates.map((u) => ({
          id: u.id,
          data: { order: { set: u.order } },
        })),
      },
      optimisticResponse: {
        updateTasks: updates.map((u) => ({
          id: u.id,
          order: u.order!,
          title: u.title,
          html: u.html,
          __typename: "Task",
          markedComplete: false,
          startDate: null,
          deadlineDate: null,
        })),
      },
    });
  };

  const confirmDelete = (cb: any, item: string) => {
    Modal.confirm({
      width: 600,
      icon: <ExclamationCircleOutlined />,
      content: <Typography.Text>{`Are you sure you want to delete this ${item}?`}</Typography.Text>,
      onOk: async function () {
        cb();
      },
      onCancel() {},
    });
  };

  const findPhaseById = (phases: any[], phaseId: number): any | undefined =>
    phases.find((p) => p.id === phaseId);

  const applyDefaultFontToPlainText = (text: string, defaultFont: string | undefined) => {
    if (text.includes("font-family:")) {
      return text;
    }
    const fontName = defaultFont?.split("=")[1];
    if (!fontName) {
      return text;
    }
    return `<div style="font-family: ${fontName};">${text}</div>`;
  };

  // Wrapper functions that match the expected component props
  const handleCreatePhase = async () => {
    await createPhase({
      variables: {
        matterId,
        data: {
          title: "",
          description: "[]",
          matter: { connect: { id: matterId } },
          order: 0, // This will be replaced with actual phase count in component
        },
      },
      update: (cache, curr) => {
        const target = curr.data?.createAndAddPhase;
        if (!target) return;
        const newPhaseRef = cache.writeFragment({
          data: target,
          fragment: PHASE_FRAGMENT,
          fragmentName: "PhaseParts",
        });
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases(existingWorkRefs = [], { readField }: any) {
              if (existingWorkRefs.some((ref: any) => readField("id", ref) === target?.id)) {
                return existingWorkRefs;
              }
              return [...existingWorkRefs, newPhaseRef];
            },
          },
        });
      },
    });
  };

  const handleCreatePhases = async (
    phases: { phase: string; tasks: string[] }[],
    sortedPhases: any[],
    defaultFont?: string
  ) => {
    const optimisticPhases = phases.map((p, index) => ({
      id: -1 * (index + 1),
      title: p.phase,
      html: applyDefaultFontToPlainText(p.phase, defaultFont),
      description: "[]",
      order: index + 1,
      tasks: p.tasks.map((t, i) => ({
        id: -1 * (1000 + index * 100 + i),
        title: t,
        html: applyDefaultFontToPlainText(t, defaultFont),
        order: i + 1,
        __typename: "Task" as const,
        // Add missing Task properties
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        phaseId: -1 * (index + 1),
        markedComplete: false,
        startDate: null,
        deadlineDate: null,
      })),
      work: [],
      __typename: "Phase" as const,
      autoFocus: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      matterId: matterId,
      markedComplete: false,
      completionPercentage: null,
      priceOptions: null,
      startDate: null,
      deadlineDate: null,
    }));

    await deletePhases({
      variables: { phaseIds: sortedPhases.map((p) => p.id) },
      optimisticResponse: {
        deletePhases: {
          phasesIds: sortedPhases.map((p) => p.id),
          __typename: "DeletePhasesResult",
        },
      },
      update: (cache) => {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases() {
              return optimisticPhases.map((phase) =>
                cache.writeFragment({
                  data: phase,
                  fragment: PHASE_FRAGMENT,
                  fragmentName: "PhaseParts",
                })
              );
            },
          },
        });
      },
    });

    await createPhases({
      variables: {
        matterId,
        data: phases.map((p, index) => ({
          title: p.phase,
          html: applyDefaultFontToPlainText(p.phase, defaultFont),
          tasks: {
            create: p.tasks.map((t, i) => ({
              title: t,
              html: applyDefaultFontToPlainText(t, defaultFont),
              order: i + 1,
            })),
          },
          description: "[]",
          matter: { connect: { id: matterId } },
          order: index + 1,
        })),
      },
      optimisticResponse: {
        createAndAddPhases: optimisticPhases as any,
      },
      update: (cache, curr) => {
        const targets = curr.data?.createAndAddPhases;
        if (!targets) return;
        const newPhaseRefs = targets.map((target: any) =>
          cache.writeFragment({
            data: target,
            fragment: PHASE_FRAGMENT,
            fragmentName: "PhaseParts",
          })
        );
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases(existingWorkRefs = [], { readField }: any) {
              // Filter out any optimistic phases (with negative IDs)
              const filteredRefs = existingWorkRefs.filter((ref: any) => readField("id", ref) > 0);
              return [...filteredRefs, ...newPhaseRefs];
            },
          },
        });
      },
    });
  };

  const handleUpdatePhase = async (phase: PhaseBase, sortedPhases: any[]) => {
    const originalPhase = findPhaseById(sortedPhases, phase.id!);
    if (!originalPhase)
      throw new Error(`Could not find original phase (id: ${phase.id}) on matter (id ${matterId}`);

    await updatePhase({
      variables: {
        phaseId: phase.id,
        matterId,
        data: {
          ...(phase.description && { description: { set: phase.description } }),
          ...(phase.title && { title: { set: phase.title } }),
          ...(phase.html && { html: { set: phase.html } }),
          ...(phase.startDate && { startDate: { set: phase.startDate } }),
          ...(phase.deadlineDate && { deadlineDate: { set: phase.deadlineDate } }),
        },
      },
      update: (cache, newItem) => {
        if (!newItem.data?.updatePhase) return;
        const newPhase = newItem.data.updatePhase;
        const newPhaseRef = cache.writeFragment({
          data: { ...newPhase },
          fragment: PHASE_FRAGMENT,
        });
        cache.modify({
          id: `Phase:${newPhase.id}`,
          fields: {
            tasks(existingPhaseRefs = [], { readField }: any) {
              if (existingPhaseRefs.some((ref: any) => readField("id", ref) === newPhase.id)) {
                return existingPhaseRefs;
              }
              return [...existingPhaseRefs, newPhaseRef];
            },
          },
        });
      },
    });
  };

  const handleDeletePhase = (phaseId: number) => {
    confirmDelete(
      () =>
        deletePhase({
          variables: { phaseId },
          optimisticResponse: { deletePhase: { phaseId, __typename: "DeletePhaseResult" } },
        }),
      "phase"
    );
  };

  const handleCreateTask = (phaseId: number, taskCount: number) => {
    createTask({
      variables: {
        phaseId,
        data: {
          title: "",
          order: taskCount + 1,
          phase: { connect: { id: phaseId } },
        },
      },
      update: createTaskCacheAdder(phaseId, taskCount + 1) as any,
    });
  };

  const handleCreateTasks = (
    taskNames: string[],
    phaseId: number,
    sortedPhases: any[],
    defaultFont?: string
  ) => {
    deleteTasks({
      variables: { phaseId: phaseId },
      update: (cache) => {
        cache.modify({
          id: `Phase:${phaseId}`,
          fields: {
            tasks(existingTaskRefs = [], { readField }: any) {
              return existingTaskRefs.filter(
                (taskRef: any) =>
                  !sortedPhases
                    .find((p) => p.id === phaseId)
                    ?.tasks?.some((t: any) => t.id === readField("id", taskRef))
              );
            },
          },
        });
      },
    }).then(() => {
      createTasks({
        variables: {
          phaseId: phaseId,
          data: taskNames.map((name, index) => ({
            title: name,
            html: applyDefaultFontToPlainText(name, defaultFont),
            phase: { connect: { id: phaseId } },
            order: index + 1,
          })),
        },
        update: (cache, { data }) => {
          if (data?.createAndAddTasks) {
            cache.modify({
              id: `Phase:${phaseId}`,
              fields: {
                tasks() {
                  return [];
                },
              },
            });
            const newTasks = data.createAndAddTasks;
            newTasks.forEach((task) => {
              const newTaskRef = cache.writeFragment({
                data: task,
                fragment: TASK_FRAGMENT,
              });
              cache.modify({
                id: `Phase:${phaseId}`,
                fields: {
                  tasks(existingTaskRefs = []) {
                    return [...existingTaskRefs, newTaskRef];
                  },
                },
              });
            });
          }
        },
      });
    });
  };

  const handleDeleteTasks = (phaseId: number, sortedPhases: any[]) => {
    deleteTasks({
      variables: { phaseId: phaseId },
      update: (cache) => {
        cache.modify({
          id: `Phase:${phaseId}`,
          fields: {
            tasks(existingTaskRefs = [], { readField }: any) {
              return existingTaskRefs.filter(
                (taskRef: any) =>
                  !sortedPhases
                    .find((p) => p.id === phaseId)
                    ?.tasks?.some((t: any) => t.id === readField("id", taskRef))
              );
            },
          },
        });
      },
    });
  };

  const handleDeleteTask = (taskId: number, phaseId: number) => {
    confirmDelete(
      () =>
        deleteTask({
          variables: { taskId },
          update: createTaskCacheRemover(phaseId),
        }),
      "task"
    );
  };

  const handleUpdateTask = (task: Partial<Task>, phaseId: number) => {
    updateTask({
      variables: {
        taskId: task.id!,
        data: {
          ...(task.title && { title: { set: task.title } }),
          ...(task.html && { html: { set: task.html } }),
          ...(task.startDate && { startDate: { set: task.startDate } }),
          ...(task.deadlineDate && { deadlineDate: { set: task.deadlineDate } }),
        },
      },
    });
  };

  const handleCreateFeeEarner = (phaseId: number) => {
    createWork({
      variables: { data: { phase: { connect: { id: phaseId } } } },
      update: (cache, curr) => {
        const target = curr.data?.createAndAddWork[0];
        const newWorkRef = cache.writeFragment({
          data: target,
          fragment: WORK_FRAGMENT,
        });
        cache.modify({
          id: `Phase:${phaseId}`,
          fields: {
            work(existingWorkRefs = [], { readField }: any) {
              if (existingWorkRefs.some((ref: any) => readField("id", ref) === target?.id)) {
                return existingWorkRefs;
              }
              return [...existingWorkRefs, newWorkRef];
            },
          },
        });
      },
    });
  };

  const handleDeleteFeeEarner = (workId: number, phaseId: number) => {
    confirmDelete(
      () =>
        deleteWork({
          variables: { workId },
          update: createWorkCacheRemover(phaseId),
          optimisticResponse: { deleteWork: { workId, __typename: "DeleteWorkResult" } },
        }),
      "work"
    );
  };

  const handleUpdateFeeEarner = (
    work: any,
    phaseId: number,
    isTimeEntryUpdate: boolean,
    sortedPhases: any[]
  ) => {
    const isFeeEarnerLevelRate = !work.feeEarnerId;

    const originalPhase = findPhaseById(sortedPhases, phaseId);
    const originalWork = originalPhase?.work?.find((w: any) => w.id === work.id!);

    let updateObject: any = undefined;

    if (isFeeEarnerLevelRate && !isTimeEntryUpdate) {
      updateObject = { disconnect: {} };
    } else if (work.feeEarnerId) {
      updateObject = { connect: { id: work.feeEarnerId } };
    }

    if (!originalWork)
      throw new Error(
        `updating work for unknown original workId: ${work.id}, phaseId: ${phaseId}, matterId: ${matterId}`
      );

    updateWork({
      variables: {
        data: {
          ...(updateObject && { feeEarner: updateObject }),
          ...(work.feeEarnerLevelId && {
            feeEarnerLevel: { connect: { id: work.feeEarnerLevelId } },
          }),
          ...(work.timeLower != null && { timeLower: { set: work.timeLower } }),
          ...(work.timeUpper != null && { timeUpper: { set: work.timeUpper } }),
          ...(work.timeSingle != null && { timeSingle: { set: work.timeSingle } }),
          ...(work.selectedRateId && {
            selectedRate: { connect: { id: work.selectedRateId } },
          }),
        },
        workId: work.id,
      },
      update: (cache, { data }) => {
        cache.updateQuery<findMatter>({ query: FIND_MATTER }, (currentData) => {
          if (currentData) {
            return produce(currentData, (draft: any) => {
              const phaseTarget = draft.findMatter.phases
                ?.find((x: any) => x.id === phaseId)
                ?.work?.find((x: any) => x.id === work.id);

              if (phaseTarget) {
                Object.assign(phaseTarget, data?.updateWork);
              }
            });
          }
        });
      },
    });
  };

  const handleResetPhases = (sortedPhases: any[]) => {
    deletePhases({
      variables: { phaseIds: sortedPhases.map((p) => p.id) },
      update: (cache) => {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases(existingPhaseRefs = [], { readField }: any) {
              return existingPhaseRefs.filter(
                (phaseRef: any) => !sortedPhases.some((p) => p.id === readField("id", phaseRef))
              );
            },
          },
        });
      },
    });
  };

  const handleUpdatePhasePrice = (
    phaseId: number,
    value: number,
    pricingType: any,
    field: string,
    sortedPhases: any[]
  ) => {
    const originalPhase = findPhaseById(sortedPhases, phaseId);
    if (!originalPhase)
      throw new Error(`Could not find original phase (id: ${phaseId}) on matter (id ${matterId}`);

    updatePhase({
      variables: {
        phaseId,
        matterId,
        data: {
          priceOptions: {
            update: {
              data: {
                ...(setSingleOrRangeProps(
                  pricingType,
                  field as "single" | "lower" | "upper",
                  value,
                  "chosen"
                ) as any),
              },
            },
          },
        },
      },
    });
  };

  const copyPhasesFromMatter = (
    sourceMatterId: number,
    sortedPhases: Array<{ id: number }> = []
  ) => {
    return deletePhases({
      variables: { phaseIds: sortedPhases.map((p: { id: number }) => p.id) },
      update: (cache) => {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            phases(existingPhaseRefs = [], { readField }: any) {
              return existingPhaseRefs.filter(
                (phaseRef: any) =>
                  !sortedPhases.some((p: { id: number }) => p.id === readField("id", phaseRef))
              );
            },
          },
        });
      },
    }).then(() => {
      return copyPhasesMutation({
        variables: {
          targetMatterId: matterId,
          copiedMatterId: sourceMatterId,
        },
        update(cache, { data }) {
          const copiedMatterPhases = data?.copyMatterPhases;
          if (!copiedMatterPhases) {
            return;
          }

          // Update phases and disbursements in the FIND_MATTER query
          cache.updateQuery<findMatter, findMatterVariables>(
            {
              query: FIND_MATTER,
              variables: { id: matterId },
            },
            (data) => {
              if (!data) return;
              return {
                findMatter: {
                  ...data.findMatter,
                  phases: [...copiedMatterPhases.phases],
                  disbursements: [...copiedMatterPhases.disbursements],
                },
              };
            }
          );

          // Update assumptions
          cache.updateQuery(
            { query: MATTER_ASSUMPTIONS_QUERY, variables: { input: matterId } },
            (currentData: any) => {
              if (!currentData) return;
              return {
                matterAssumptions: copiedMatterPhases.assumptions.map((assumption: any) => ({
                  ...assumption,
                  __typename: "Assumption",
                  autoFocus: false,
                  matterId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  default: false,
                  mandatory: false,
                })),
              };
            }
          );

          // Update exclusions
          cache.updateQuery(
            { query: MATTER_EXCLUSIONS_QUERY, variables: { input: matterId } },
            (currentData: any) => {
              if (!currentData) return;
              return {
                matterExclusions: copiedMatterPhases.exclusions.map((exclusion: any) => ({
                  ...exclusion,
                  __typename: "Exclusion",
                  autoFocus: false,
                  matterId,
                  default: false,
                  mandatory: false,
                  fromLibraryExclusionId: null,
                })),
              };
            }
          );
        },
      });
    });
  };

  const copyPhaseToMatter = (phaseId: number) => {
    // Find current max order from existing phases
    return copyPhaseToMatterMutation({
      variables: {
        phaseId,
        matterId,
      },
    });
  };

  return {
    // Basic mutation functions
    createPhase,
    createPhases,
    updatePhase,
    updatePhases,
    deletePhase,
    deletePhases,
    createTask,
    createTasks,
    updateTask,
    updateTasks,
    deleteTask,
    deleteTasks,
    createWork,
    updateWork,
    deleteWork,

    // Higher-level wrapper functions with better interfaces
    handleCreatePhase,
    handleCreatePhases,
    handleUpdatePhase,
    handleDeletePhase,
    handleCreateTask,
    handleCreateTasks,
    handleDeleteTasks,
    handleDeleteTask,
    handleUpdateTask,
    handleCreateFeeEarner,
    handleDeleteFeeEarner,
    handleUpdateFeeEarner,
    handleResetPhases,
    handleUpdatePhasePrice,

    // Helper functions
    autoFocusOff,
    confirmDelete,
    findPhaseById,
    applyDefaultFontToPlainText,
    sortById,
    sortByOrder,

    // Reordering functions
    onReorderPhase,
    onReorderTask,

    // Copy phases functions
    copyPhasesFromMatter,
    copyPhaseToMatter,

    // Loading states
    loading: {
      copyPhasesLoading,
      copyPhaseLoading,
      createPhaseLoading,
      createPhasesLoading,
      updatePhaseLoading,
      updatePhasesLoading,
      deletePhaseLoading,
      deletePhasesLoading,
      createTaskLoading,
      createTasksLoading,
      updateTaskLoading,
      updateTasksLoading,
      deleteTaskLoading,
      deleteTasksLoading,
      createWorkLoading,
      updateWorkLoading,
      deleteWorkLoading,
    },
  };
}
